import * as signalR from '@microsoft/signalr';
import { pavoSignalRService } from './pavoSignalRService';
import type { PavoPayCustomerParty } from '../components/salesSystem/quickSale/hooks/fiscalDevices/pavopay';

const PAVO_API_URL = process.env.REACT_APP_GTS_PAVO_API_URL || 'http://localhost:5011';

// ════════════════════════════════════════════════════════════════
// REST-P2P Timeout politikası
//
// Tek ödeme (cash/pos/external): 100s — Pavo intent kendisi 90s+10s
// timeout uyguluyor; web 100s'ye ayarlanırsa Pavo bitmeden modal kapanmaz
// ama bitince hemen reconciliation tetiklenebilir.
//
// Karma ödeme (mixed): 180s — birden fazla parça toplam süresi daha uzun.
// Çağıran (`usePayment.ts`) `timeoutMs` override eder.
//
// Timeout veya SignalR/presence kopuşu olduğunda DB polling YERİNE
// `resolveQuickSaleResult` (getSaleResult zinciri) tetiklenir; bu, cihazdan
// gerçek satış durumunu doğrulayan en güvenilir kaynak.
// ════════════════════════════════════════════════════════════════

const DEFAULT_TIMEOUT_SINGLE_MS = 100_000;
/** resolveQuickSaleResult kendi içinde 15s bekler — Pavo `getSaleResult` busy retry'ı 8s+15s yapabilir. */
const RESOLVE_TIMEOUT_MS = 25_000;

// F7: Sayfa yenilense/HMR olsa bile pending request'in bilinmesi icin
const RESTP2P_PENDING_KEY = 'restP2PPendingRequestId';

function setPendingRequestId(requestId: string | null) {
  try {
    if (requestId) localStorage.setItem(RESTP2P_PENDING_KEY, requestId);
    else localStorage.removeItem(RESTP2P_PENDING_KEY);
  } catch (_) {}
}

export function getPendingRestP2PRequestId(): string | null {
  try { return localStorage.getItem(RESTP2P_PENDING_KEY); } catch (_) { return null; }
}

// ════════════════════════════════════════════════════════════════
// Reconciliation güvenlik kontrolü — yakın geçmişte görülen satış numaraları.
//
// Cihazın `getSaleResult(OrderNo=requestId)` cevabı yanlış satışla eşleşirse
// (örn. firmware bilinmeyen OrderNo için son tamamlanan satışı dönerse)
// reconciliation, kart daha okutulmadan sahte "completed" üretebilir.
// Savunma: completed kabul edilen her satış numarası burada kaydedilir;
// reconciliation cevabındaki satış no DAHA ÖNCE kullanıldıysa veya tutar
// istenen tutarla uyuşmuyorsa başarı SAYILMAZ, manuel kontrol istenir.
// ════════════════════════════════════════════════════════════════

const RESTP2P_SEEN_SALES_KEY = 'restP2PSeenSaleNumbers';
const SEEN_SALE_TTL_MS = 12 * 60 * 60 * 1000;
const SEEN_SALE_MAX = 200;

function readSeenSaleNumbers(): Array<{ sn: string; at: number }> {
  try {
    const raw = localStorage.getItem(RESTP2P_SEEN_SALES_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    const cutoff = Date.now() - SEEN_SALE_TTL_MS;
    return list.filter((e: any) => e && typeof e.sn === 'string' && typeof e.at === 'number' && e.at >= cutoff);
  } catch (_) {
    return [];
  }
}

function recordSeenSaleNumber(saleNumber: string | null | undefined) {
  const sn = (saleNumber ?? '').trim();
  if (!sn) return;
  try {
    const list = readSeenSaleNumbers();
    if (list.some((e) => e.sn === sn)) return;
    list.push({ sn, at: Date.now() });
    localStorage.setItem(
      RESTP2P_SEEN_SALES_KEY,
      JSON.stringify(list.slice(-SEEN_SALE_MAX)),
    );
  } catch (_) {}
}

function wasSaleNumberSeenBefore(saleNumber: string | null | undefined): boolean {
  const sn = (saleNumber ?? '').trim();
  if (!sn) return false;
  return readSeenSaleNumbers().some((e) => e.sn === sn);
}

/** Cihazın ham completed payload'ından satış numarasını çıkar (casing toleranslı). */
function extractSaleNumber(result: unknown): string | null {
  const r = result as any;
  const sn = r?.PavoSaleNumber || r?.pavoSaleNumber || r?.SaleNumber || r?.saleNumber;
  const s = typeof sn === 'string' ? sn.trim() : '';
  return s.length > 0 ? s : null;
}

/**
 * F7: Backend'den payment request detayini cek. Timeout / kurtarma yollarinda kullanilir.
 */
export async function fetchPaymentStatus(requestId: string): Promise<{
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'unknown';
  pavoSaleNumber?: string | null;
  errorMessage?: string | null;
  paymentResult?: any;
  totalAmount?: number | null;
}> {
  try {
    const res = await fetch(`${PAVO_API_URL}/api/PavoPayment/${encodeURIComponent(requestId)}`);
    if (!res.ok) return { status: 'unknown' };
    const body = await res.json();
    const p = body?.Payment || body?.payment;
    if (!p) return { status: 'unknown' };
    let parsedResult: any = null;
    const rJson = p.PaymentResultJson || p.paymentResultJson;
    if (typeof rJson === 'string' && rJson.length > 0) {
      try { parsedResult = JSON.parse(rJson); } catch (_) { parsedResult = rJson; }
    } else if (rJson && typeof rJson === 'object') {
      parsedResult = rJson;
    }
    const statusRaw = String(p.Status || p.status || 'unknown').toLowerCase();
    return {
      status: (['pending', 'processing', 'completed', 'failed', 'cancelled'].includes(statusRaw)
        ? statusRaw
        : 'unknown') as any,
      pavoSaleNumber: p.PavoSaleNumber || p.pavoSaleNumber || null,
      errorMessage: p.ErrorMessage || p.errorMessage || null,
      paymentResult: parsedResult,
      totalAmount: typeof p.TotalAmount === 'number' ? p.TotalAmount : (typeof p.totalAmount === 'number' ? p.totalAmount : null),
    };
  } catch (err) {
    console.warn('[RestP2P] fetchPaymentStatus hata:', err);
    return { status: 'unknown' };
  }
}

/** Rest-P2P ödeme parçası (tek kalem). */
export interface RestP2PPaymentPart {
  method: 'cash' | 'pos';
  amount: number;
  label?: string;
  order: number;
}

/**
 * PavoPay 507 `Sale.ExternalPayments[]` icin tek kalem harici odeme bilgisi.
 *
 * Sabit kombinasyonlar (yaml satir 8230 + Payment Provider Brand tablosu):
 *   - Type=22 (Diger), Mediator=14 (Harici Odeme Saglayici)
 *   - Brand=200 Yemeksepeti / 201 Getir / 202 Trendyol / 203 Migros Yemek /
 *     204 Web Sitesi Online Odeme
 *
 * Online odenmis paket siparislerinde ("Online Fis Yazdir") `paymentMode`
 * `'external'` ile birlikte gonderilir; cihaz `PaymentInformations` yerine
 * `ExternalPayments` ile completeSale eder.
 */
export interface RestP2PExternalPayment {
  type: number;     // 22
  mediator: number; // 14
  brand: number;    // 200..204
  amount: number;
  externalReferenceText?: string;
}

/** Rest-P2P istek payload'u (backend: QuickSalePaymentPayload). */
export interface RestP2PPaymentRequest {
  requestId?: string;
  deviceCode: string;
  /**
   * 'cash' | 'pos' | 'mixed' | 'external'
   *   - 'external': Online odenmis siparis icin fis kesimi; `externalPayment`
   *     dolu olmali. Cihaz `Sale.ExternalPayments[]` ile completeSale eder.
   */
  paymentMode: 'cash' | 'pos' | 'mixed' | 'external';
  totalAmount: number;
  parts: RestP2PPaymentPart[];
  receiptItemsJson?: string | null;
  callerConnectionId?: string | null;
  companyId?: string | null;
  branchId?: string | null;
  screenId?: number | null;
  cashierName?: string | null;
  sourceLabel?: string | null;
  /**
   * Phase 2: "Fatura Türü Seçin" modalından seçilmiş müşteri bilgisi.
   * Backend → cihaz → PavoPay intent'ine `saleObject.CustomerParty` olarak
   * geçer. `MainDocumentType` sabit `1` kalır; E-Fatura vs E-Arşiv ayrımı
   * PavoPay tarafında CustomerParty'ye bakılarak otomatik yapılır.
   */
  customerParty?: PavoPayCustomerParty | null;
  /**
   * Phase 2: Müşteriye e-arşiv/e-fatura SMS'inin gönderileceği numara.
   * Dolu gelirse cihaz intent'ine `SendPhoneNotification=true` +
   * `NotificationPhone=<phone>` olarak ilave edilir.
   */
  notificationPhone?: string | null;
  /**
   * Mutfak entegrasyonu açıkken PavoPay intent'ine üst bilgi olarak
   * basılacak sipariş kısa kodu. Dolu geldiğinde + receiptCompanyName
   * ile birlikte Pavo cihazının kestiği fişin üstüne şirket adı +
   * sipariş numarası eklenir, altına "AQUASOFT" basılır.
   * Rest (doğrudan) akışının cash path'iyle birebir aynı anlam.
   */
  kitchenOrderCode?: string | null;
  /**
   * Mutfak entegrasyonu açıkken fiş üst bilgisinde görünecek şirket adı.
   * Boş/null ise sadece sipariş numarası ve takip yazısı basılır.
   */
  receiptCompanyName?: string | null;

  /**
   * Online odenmis platform siparisleri (Yemeksepeti / Trendyol / Migros /
   * Getir / Web Sitesi) icin "Online Fis Yazdir" akisinda set edilir.
   * Cihaz tarafinda PavoPay'e `Sale.ExternalPayments[]` icine yansir;
   * `paymentMode` `'external'` ile birlikte gonderilmelidir.
   * (Klasik nakit/POS akisinda null kalir.)
   */
  externalPayment?: RestP2PExternalPayment | null;
}

/** Pavo'dan dönen parça sonucu. */
export interface RestP2PPartResult {
  order: number;
  method: string;
  amount: number;
  status: string;
  errorMessage?: string | null;
}

/** Rest-P2P ödeme nihai sonuç. */
export interface RestP2PPaymentResultRaw {
  requestId: string;
  status: 'completed' | 'failed' | 'cancelled';
  errorCode?: string | null;
  errorMessage?: string | null;
  totalAmount?: number | null;
  partResults?: RestP2PPartResult[];
  pavoSaleNumber?: string | null;
  /**
   * E-arşiv/e-fatura ETTN'i (Sale.FinancialDocuments[0].DocumentNo).
   * Cihaz dispatcher'ı çıkarıp yollar; dolu gelirse fatura kaydına
   * EInvoiceETTN yazılır ve fatura "pavopay" e-belgesi olarak işaretlenir.
   * Not: SignalR ham cihaz cevabında PascalCase (`Ettn`) gelebilir —
   * tüketen taraf her iki casing'i de tolere etmeli.
   */
  ettn?: string | null;
  /** GİB fatura numarası (Sale.InvoiceNo). */
  invoiceNo?: string | null;
  /** PavoPay fatura sorgulama linki (SaleInquieryLink). */
  inquiryLink?: string | null;
}

/** Progress event (karma ödeme). */
export interface RestP2PProgressEvent {
  requestId: string;
  currentIndex: number;
  totalCount: number;
  /** "processing" | "part_completed" | "part_failed" */
  stage: string;
  lastPart?: RestP2PPartResult | null;
}

/** requestPayment promise sonucu (unified). */
export type RestP2PPaymentOutcome =
  | { status: 'completed'; requestId: string; result: RestP2PPaymentResultRaw }
  | { status: 'failed'; requestId: string; errorCode: string; message: string }
  | { status: 'cancelled'; requestId: string; reason: string };

export interface RestP2PRequestOptions {
  onProgress?: (progress: RestP2PProgressEvent) => void;
  /**
   * Pavo'dan sonuç gelmezse sürenin sonunda reconciliation tetiklenir.
   * Tek ödemede default 100s; mixed çağıran (usePayment) 180s verir.
   */
  timeoutMs?: number;
  /**
   * "Verifying..." UI state'i için. Reconciliation (getSaleResult) tetiklendiğinde
   * tetiklenir; modal kullanıcıya "Pavo cihazından ödeme durumu doğrulanıyor..."
   * gösterebilir.
   */
  // ⚠️ 'paranoid' reason ARTIK TETIKLENMIYOR (2026-05 sadelestirme).
  //    Geriye donuk uyumluluk icin tipte korunuyor; aktif sadece timeout/presence/hub-state.
  onVerifying?: (info: { reason: 'timeout' | 'presence' | 'paranoid' | 'hub-state' }) => void;
}

/** resolveQuickSaleResult dönüş değerleri. */
export type RestP2PResolveOutcome = {
  status: 'completed' | 'failed' | 'cancelled' | 'notfound' | 'unknown';
  requestId: string;
  statusId?: number | null;
  pavoSaleNumber?: string | null;
  totalPrice?: number | null;
  errorMessage?: string | null;
  rawSale?: any;
};

export interface RestP2PResolveOptions {
  /** resolve cevabını beklerken kullanılan timeout. Default 25s. */
  timeoutMs?: number;
  /** İlgili payment'in CompanyId'si (Yöntem 2 — backend body'de zorunlu). */
  companyId: string;
  /** Opsiyonel branchId. */
  branchId?: string | null;
  /** Opsiyonel: Pavo cihazındaki satış orderNo'su (boş ise requestId kullanılır). */
  orderNo?: string | null;
}

// ════════════════════════════════════════════════════════════════
// F4 — Rest-P2P iade/iptal/sorgu tipleri
// ════════════════════════════════════════════════════════════════

export interface RestP2PRefundBaseRequest {
  requestId?: string;
  deviceCode: string;
  companyId?: string | null;
  branchId?: string | null;
}

export interface RestP2PCancelSaleRequest extends RestP2PRefundBaseRequest {
  saleNumber: string;
  amount?: number | null;
  /**
   * İptal türü (Pavo `Sale.IsVoid`): true = void (anlık iptal), false = iade.
   * Verilmezse backend/cihaz tarafında `true` varsayılır.
   */
  isVoid?: boolean | null;
}

export interface RestP2PGetReturnableSaleRequest extends RestP2PRefundBaseRequest {
  saleId?: number | null;
  saleNumber?: string | null;
  saleUid?: string | null;
}

export interface RestP2PPartialReturnItem {
  RelatedSaleItemId: number;
  ItemQuantity: number;
  UnitPriceAmount: number;
  ReturnAmount: number;
}

export interface RestP2PPartialReturnPaymentInfo {
  Mediator: number;
  Amount: number;
  IsVoid: boolean;
  RelatedPaymentId?: number | null;
}

export interface RestP2PPartialReturnRequest extends RestP2PRefundBaseRequest {
  relatedSaleId: number;
  items: RestP2PPartialReturnItem[];
  paymentInfos?: RestP2PPartialReturnPaymentInfo[] | null;
  orderNo?: string | null;
}

export interface RestP2PPartialReturnForAdvanceRequest extends RestP2PRefundBaseRequest {
  relatedSaleId: number;
  totalRefundAmount: number;
  paymentInfos: RestP2PPartialReturnPaymentInfo[];
  saleItems?: { Id: number; Name?: string; VatRate: number }[] | null;
}

/**
 * PYK "Bakiye Yükle (POS)" akışı için Avans Satış (AdvanceSale) intent isteği.
 * Pavo intent: pavopay.intent.action.advance.sale
 *
 * Cihaz cevabı `result` alanında `saleNumber`, `orderNo`, `invoiceNo`,
 * `saleId`, `saleUid`, `payments`, `rawSale` ve `fiscalTransactionData`
 * (JSON string) taşır — bunlar `processAdvanceSale` helper'ı tarafından
 * AdvanceSaleResult'a map edilir.
 */
export interface RestP2PAdvanceSaleRequest extends RestP2PRefundBaseRequest {
  amount: number;
  reason: string;
  paymentMediator: number;
  /** Frontend'de üretilen takip numarası (ör. `PYK-${Date.now()}-${rand}`). */
  orderNo?: string;
  /** E-Fatura/E-Arşiv için cihaz Sale.CustomerParty alanına yazılır. */
  customerParty?: Record<string, unknown>;
}

export type RestP2PRefundAction =
  | 'cancelSale'
  | 'getReturnableSale'
  | 'partialReturn'
  | 'partialReturnForAdvanceSale'
  | 'advanceSale';

export type RestP2PRefundOutcome =
  | { status: 'completed'; requestId: string; action: RestP2PRefundAction; result: any }
  | { status: 'failed'; requestId: string; action: RestP2PRefundAction; errorCode: string; message: string };

/**
 * Rest-P2P ödeme servisi. Kullanıcı NAKIT/POS/Karma butonuna bastığında
 * Pavo cihazına SignalR üzerinden ödeme gönderir ve sonucu bekler.
 */
class RestP2PPaymentService {
  /**
   * Rest-P2P icin SignalR baglantisinin aktif olmasini garanti eder.
   * Bagli degilse companyId/branchId ile baglanir. Baglanti ID uretilene
   * kadar kisa bir grace period bekler (connectionId null donmesin diye).
   */
  private async ensureSignalRConnected(
    companyId?: string | null,
    branchId?: string | null,
  ): Promise<string | null> {
    try {
      if (!pavoSignalRService.isConnected) {
        if (companyId) {
          await pavoSignalRService.connect(companyId, branchId ?? undefined);
        } else {
          console.warn('[RestP2P] SignalR bagli degil ve companyId verilmemis; CallerConnectionId bos gidecek');
          return pavoSignalRService.getConnectionId();
        }
      }
      // Baglanti kurulmus olsa bile connectionId anlik olarak null olabilir — kisa bir bekleme
      for (let i = 0; i < 10; i++) {
        const id = pavoSignalRService.getConnectionId();
        if (id) return id;
        await new Promise((r) => setTimeout(r, 100));
      }
      return pavoSignalRService.getConnectionId();
    } catch (err) {
      console.warn('[RestP2P] SignalR baglanti kurulamadi:', err);
      return pavoSignalRService.getConnectionId();
    }
  }

  /**
   * Pavo cihazına ödeme isteği yolla, sonucu SignalR ile bekle.
   *
   * Davranış:
   *   - SignalR `OnQuickSalePaymentCompleted/Failed/Cancelled` event gelirse
   *     ilk olarak modal'a sonuç döner; ayrıca PARANOID mod'da `getSaleResult`
   *     reconciliation tetiklenir, sonuç çelişirse cihazdaki gerçek durum kabul.
   *   - Timeout (default 100s tek / 180s mixed) → DB polling YERİNE
   *     `resolveQuickSaleResult` tetiklenir; cihaz gerçek durumu döner.
   *   - SignalR hub Disconnected/Reconnecting ya da cihaz presence offline'a
   *     düşerse → anında `resolveQuickSaleResult` tetiklenir.
   */
  async requestPayment(
    request: RestP2PPaymentRequest,
    options: RestP2PRequestOptions = {},
  ): Promise<RestP2PPaymentOutcome> {
    const requestId = request.requestId && request.requestId.length > 0
      ? request.requestId
      : generateUuid();

    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_SINGLE_MS;

    // SignalR baglantisini garanti et ve CallerConnectionId'yi otomatik doldur
    const callerConnectionId =
      request.callerConnectionId ??
      (await this.ensureSignalRConnected(request.companyId, request.branchId));
    if (!callerConnectionId) {
      console.warn('[RestP2P] CallerConnectionId bos — Pavo sonucu sadece fallback broadcast ile gelebilir');
    }

    const payload = {
      RequestId: requestId,
      DeviceCode: request.deviceCode,
      PaymentMode: request.paymentMode,
      TotalAmount: Number(request.totalAmount.toFixed(2)),
      Parts: request.parts.map((p) => ({
        Method: p.method,
        Amount: Number(p.amount.toFixed(2)),
        Label: p.label ?? null,
        Order: p.order,
      })),
      ReceiptItemsJson: request.receiptItemsJson ?? null,
      CallerConnectionId: callerConnectionId,
      CompanyId: request.companyId ?? null,
      BranchId: request.branchId ?? null,
      ScreenId: request.screenId ?? null,
      CashierName: request.cashierName ?? null,
      SourceLabel: request.sourceLabel ?? null,
      // Phase 2: CustomerParty + NotificationPhone (backend aynen cihaza aktarir)
      CustomerParty: request.customerParty
        ? serializeCustomerParty(request.customerParty)
        : null,
      NotificationPhone: request.notificationPhone ?? null,
      // Mutfak entegrasyonu: Pavo fişi üst/alt bilgileri için payload'a geçer
      KitchenOrderCode: request.kitchenOrderCode ?? null,
      ReceiptCompanyName: request.receiptCompanyName ?? null,
      // Harici Odeme (Online Fis Yazdir): cihaz `Sale.ExternalPayments` ile
      // fis keser; `PaymentInformations` gonderilmez.
      ExternalPayment: serializeExternalPayment(request.externalPayment),
    };

    // F7: Yenilenme sonrasi kurtarma icin pending requestId'yi kaydet
    setPendingRequestId(requestId);

    return new Promise<RestP2PPaymentOutcome>((resolve) => {
      const subId = `restp2p-${requestId}`;
      const presenceSubId = `restp2p-presence-${requestId}`;
      const hubStateSubId = `restp2p-hubstate-${requestId}`;
      let settled = false;
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
      let resolveInFlight = false;

      const cleanup = () => {
        pavoSignalRService.removeQuickSaleCallbacks(subId);
        pavoSignalRService.removeDevicePresenceSubscription(request.deviceCode, presenceSubId);
        // Hub state aboneliği: removeStatusChange API yok, kapatma için sentinel pattern
        // (callback içinde settled kontrolü zaten var, ileride hub.disconnect olunca
        // listener kümesi temizleniyor).
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
      };

      const settle = (outcome: RestP2PPaymentOutcome) => {
        if (settled) return;
        settled = true;
        cleanup();
        // F7: Settle oldugunda pending'i temizle
        setPendingRequestId(null);
        resolve(outcome);
      };

      // Reconciliation tetikleyici: SADECE timeout / presence / hub-state
      // yollarında çağrılır; cihaz cevabı hiç gelmediğinde getSaleResult ile
      // sorgular ve modal'i settle eder.
      //
      // ⚠️ 'paranoid' (basarili cevap sonrasi cift kontrol) ARTIK TETIKLENMIYOR:
      //    bkz. onQuickSalePaymentCompleted/Failed/Cancelled callback'leri.
      //    Cihaz Pavo zaten odemeyi commit edip cevap donuyor; ekstra sorgu
      //    Pavo uygulamasini foreground'a getirip UI flickering yaratiyordu.
      const triggerReconciliation = async (
        reason: 'timeout' | 'presence' | 'paranoid' | 'hub-state',
        _successHint?: RestP2PPaymentOutcome,
      ) => {
        if (settled) return;
        if (resolveInFlight) return;
        // Defansif: paranoid yine de cagrilirsa (eski code path), bilincli olarak
        // hicbir sey yapmiyoruz. Settle edilmis ise getSaleResult Pavo'yu
        // tekrar acmasin.
        if (reason === 'paranoid') {
          console.log('[RestP2P] paranoid reconciliation skip (artik etkin degil)');
          return;
        }
        resolveInFlight = true;

        try {
          options.onVerifying?.({ reason });
        } catch (e) {
          console.warn('[RestP2P] onVerifying handler hata:', e);
        }

        if (!request.companyId) {
          console.warn('[RestP2P] Reconciliation: companyId yok, fallback failure');
          if (!settled) {
            settle({
              status: 'failed',
              requestId,
              errorCode: 'COMPANY_ID_MISSING',
              message: 'CompanyId yok, reconciliation yapılamadı',
            });
          }
          return;
        }

        let outcome: RestP2PResolveOutcome | null = null;
        try {
          outcome = await this.resolveQuickSaleResult(requestId, request.deviceCode, {
            companyId: request.companyId,
            branchId: request.branchId,
          });
          console.log(
            '[RestP2P] 🔄 reconciliation outcome:',
            outcome?.status,
            'reason=', reason,
            'requestId=', requestId,
          );
        } catch (e) {
          console.warn('[RestP2P] resolveQuickSaleResult exception:', e);
        }

        if (settled) return;
        if (!outcome) {
          settle({
            status: 'failed',
            requestId,
            errorCode: 'RECONCILIATION_TIMEOUT',
            message:
              'Pavo cihazından doğrulama alınamadı, manuel kontrol gerekli. Cihaz ekranını kontrol edin.',
          });
          return;
        }
        if (outcome.status === 'completed') {
          // ⚠️ SAHTE-BAŞARI SAVUNMASI: reconciliation cevabı cihaz firmware'inin
          // getSaleResult eşleşmesine dayanır; yanlış satış dönerse kart daha
          // okutulmadan "completed" üretilebilir. İki kontrol:
          //   1) Tutar: cihazın döndüğü TotalPrice istenen tutarla uyuşmalı.
          //   2) Satış no: aynı satış numarası yakın geçmişte zaten completed
          //      kabul edildiyse bu YENİ ödemenin cevabı olamaz.
          const resolvedSn = outcome.pavoSaleNumber?.trim() || null;
          const amountMismatch =
            typeof outcome.totalPrice === 'number' &&
            isFinite(outcome.totalPrice) &&
            request.totalAmount > 0 &&
            Math.abs(outcome.totalPrice - request.totalAmount) > 0.01;
          const saleNumberReused = wasSaleNumberSeenBefore(resolvedSn);

          if (amountMismatch || saleNumberReused) {
            console.warn(
              '[RestP2P] 🚨 Reconciliation completed REDDEDİLDİ:',
              amountMismatch
                ? `tutar uyuşmuyor (cihaz=${outcome.totalPrice}, istenen=${request.totalAmount})`
                : `satış no daha önce kullanılmış (${resolvedSn})`,
              'requestId=', requestId,
            );
            settle({
              status: 'failed',
              requestId,
              errorCode: 'RECONCILIATION_MISMATCH',
              message:
                'Pavo cihazından gelen doğrulama bu ödemeyle eşleşmedi. Ödeme ALINMAMIŞ olabilir — cihaz ekranından ve gün sonu raporundan kontrol edin, gerekiyorsa tekrar deneyin.',
            });
            return;
          }

          recordSeenSaleNumber(resolvedSn);
          settle({
            status: 'completed',
            requestId,
            result: {
              requestId,
              status: 'completed',
              totalAmount: outcome.totalPrice ?? undefined,
              pavoSaleNumber: outcome.pavoSaleNumber ?? undefined,
              // Tam satış Data'sı — ETTN (FinancialDocuments[0].DocumentNo)
              // tüketen tarafça buradan çıkarılabilir.
              rawSaleJson: outcome.rawSale ?? undefined,
              recoveredBy: `resolve_${reason}`,
            } as any,
          });
        } else if (outcome.status === 'cancelled') {
          settle({
            status: 'cancelled',
            requestId,
            reason: outcome.errorMessage || 'Pavo cihazı iptal etti',
          });
        } else if (outcome.status === 'notfound') {
          settle({
            status: 'failed',
            requestId,
            errorCode: 'NOT_FOUND',
            message: 'Pavo cihazında satış bulunamadı — ödeme alınmamış.',
          });
        } else if (outcome.status === 'failed') {
          settle({
            status: 'failed',
            requestId,
            errorCode: 'FAILED',
            message: outcome.errorMessage || 'Pavo ödeme başarısız',
          });
        } else {
          // unknown
          settle({
            status: 'failed',
            requestId,
            errorCode: 'UNKNOWN',
            message:
              'Pavo cihazından doğrulama alınamadı, manuel kontrol gerekli.',
          });
        }
      };

      // ⚠️ PARANOID RECONCILIATION KALDIRILDI (2026-05):
      //   Önceden her SignalR cevabından sonra `triggerReconciliation('paranoid')`
      //   tetikleniyordu (getSaleResult intent → Pavo cihazı tekrar foreground'a
      //   geliyordu). PavoPay zaten ödemeyi cihazda commit edip StatusId=4 ile
      //   cevap dönüyor; bu doğrulama gereksiz olduğu gibi:
      //     - Pavo uygulamasını ödeme tamamlandıktan sonra tekrar açıyordu
      //       (UI flickering, kullanıcıya "ekran neden açılıp kapandı?" sorusu).
      //     - `getSaleResult` arada StatusId=8 (transaction finished with error)
      //       gibi yanıltıcı durumlar dönüp false-positive "paranoid mismatch"
      //       log'larına neden oluyordu (asıl ödeme başarılıyken).
      //   Cihaz cevabı tek doğru kaynak; SignalR success/fail/cancel direkt kabul.
      //   `timeout / presence / hub-state` reconciliation'ları gerçek anomali
      //   senaryoları olduğu için KORUNUYOR (cevabın hiç gelmediği durumlar).
      pavoSignalRService.onQuickSalePaymentCompleted(subId, (rid, result) => {
        if (rid !== requestId) return;
        // Cihazın gerçek cevabı — satış no'yu kaydet ki sonraki bir reconciliation
        // aynı satışı yanlışlıkla YENİ bir ödemenin başarısı sayamasın.
        recordSeenSaleNumber(extractSaleNumber(result));
        const outcome: RestP2PPaymentOutcome = {
          status: 'completed',
          requestId,
          result: result as RestP2PPaymentResultRaw,
        };
        settle(outcome);
      });

      pavoSignalRService.onQuickSalePaymentFailed(subId, (rid, errorCode, message) => {
        if (rid !== requestId) return;
        const outcome: RestP2PPaymentOutcome = {
          status: 'failed',
          requestId,
          errorCode: errorCode || 'UNKNOWN',
          message: message || 'Pavo ödeme başarısız',
        };
        settle(outcome);
      });

      pavoSignalRService.onQuickSalePaymentCancelled(subId, (rid, reason) => {
        if (rid !== requestId) return;
        const outcome: RestP2PPaymentOutcome = {
          status: 'cancelled',
          requestId,
          reason: reason && reason.length > 0 ? reason : 'Pavo cihazı iptal etti',
        };
        settle(outcome);
      });

      if (options.onProgress) {
        pavoSignalRService.onQuickSalePaymentProgress(subId, (rid, progress) => {
          if (rid !== requestId) return;
          try {
            options.onProgress!(progress as RestP2PProgressEvent);
          } catch (e) {
            console.error('[RestP2P] onProgress callback hatası:', e);
          }
        });
      }

      // Presence: cihaz disconnect olursa anında reconciliation tetikle.
      pavoSignalRService.addDevicePresenceSubscription(
        request.deviceCode,
        presenceSubId,
        (_dc, isOnline) => {
          if (settled || isOnline) return;
          console.warn('[RestP2P] ⚠️ Pavo cihazı disconnect oldu, reconciliation tetikleniyor:', _dc);
          void triggerReconciliation('presence');
        },
      );

      // Hub state: hub Disconnected/Reconnecting olursa cihaz cevabı ulaşamaz —
      // anında reconciliation tetikle. (Reconnect olursa snapshot ile zaten
      // toparlanır; ama bu sırada da getSaleResult düşmesi gerekiyor.)
      pavoSignalRService.onStatusChange((state) => {
        if (settled) return;
        if (
          state === signalR.HubConnectionState.Disconnected ||
          state === signalR.HubConnectionState.Reconnecting
        ) {
          // Tag ile bağla — birden fazla request varken her biri kendi başına tetikleyebilsin.
          // onStatusChange shared callback olduğu için burada doğrudan triggerReconciliation çağırıyoruz.
          console.warn('[RestP2P] ⚠️ Hub state', state, '- reconciliation tetikleniyor:', requestId);
          void triggerReconciliation('hub-state');
        }
      });
      // Bilgi: onStatusChange için subId tabanlı remove API yok, ama settled
      // kontrolü gereksiz tetiklemeyi engelliyor (race-safe).
      void hubStateSubId;

      // HTTP POST → create-quicksale-request
      (async () => {
        try {
          const res = await fetch(`${PAVO_API_URL}/api/PavoPayment/create-quicksale-request`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });

          if (!res.ok) {
            let message = 'Rest-P2P isteği başarısız';
            try {
              const body = await res.json();
              if (body?.Message) message = body.Message;
              else if (body?.message) message = body.message;
            } catch (_) {
              // json değil
            }
            const errorCode = res.status === 503 ? 'DEVICE_OFFLINE' : `HTTP_${res.status}`;
            settle({ status: 'failed', requestId, errorCode, message });
            return;
          }

          // Başarılı POST: SignalR event'lerini bekliyoruz. Hard timeout dolunca
          // DB polling YERİNE reconciliation (getSaleResult) tetiklenecek.
          timeoutHandle = setTimeout(() => {
            if (settled) return;
            console.warn('[RestP2P] ⏱️ Hard timeout doldu, reconciliation tetikleniyor:', requestId);
            void triggerReconciliation('timeout');
          }, timeoutMs);
        } catch (err: any) {
          console.error('[RestP2P] HTTP hata:', err);
          settle({
            status: 'failed',
            requestId,
            errorCode: 'NETWORK',
            message: err?.message || 'Ağ hatası',
          });
        }
      })();
    });
  }

  /**
   * REST-P2P Reconciliation — Pavo cihazına uzaktan `getSaleResult` çalıştırıp
   * gerçek satış durumunu çek.
   *
   * Akış: web -> POST /resolve-quicksale-result -> SignalR
   * `OnResolveQuickSaleResultRequest` -> Pavo -> POST /resolve-quicksale-result-complete
   * -> SignalR `OnQuickSaleResultResolved` -> web.
   *
   * Body PascalCase (Yöntem 2, explicit CompanyId).
   */
  async resolveQuickSaleResult(
    requestId: string,
    deviceCode: string,
    options: RestP2PResolveOptions,
  ): Promise<RestP2PResolveOutcome> {
    const timeoutMs = options.timeoutMs ?? RESOLVE_TIMEOUT_MS;
    const subId = `resolve-${requestId}-${Date.now()}`;

    // CallerConnectionId: backend SignalR push'unda cihazın bunu geri taşımasıyla
    // ilgili istemciye yayın yapacak.
    const callerConnectionId =
      (await this.ensureSignalRConnected(options.companyId, options.branchId)) ?? null;

    return new Promise<RestP2PResolveOutcome>((resolve) => {
      let settled = false;
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

      const cleanup = () => {
        pavoSignalRService.removeQuickSaleResultResolvedCallback(subId);
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
      };

      const settle = (outcome: RestP2PResolveOutcome) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(outcome);
      };

      pavoSignalRService.onQuickSaleResultResolved(subId, (rid, result) => {
        if (rid !== requestId) return;
        if (!result || typeof result !== 'object') {
          settle({ status: 'unknown', requestId });
          return;
        }
        const r = result as any;
        const statusRaw = String(r.Status || r.status || 'unknown').toLowerCase();
        const status = ([
          'completed',
          'failed',
          'cancelled',
          'notfound',
          'unknown',
        ].includes(statusRaw)
          ? statusRaw
          : 'unknown') as RestP2PResolveOutcome['status'];
        settle({
          status,
          requestId,
          statusId: r.StatusId ?? r.statusId ?? null,
          pavoSaleNumber: r.PavoSaleNumber ?? r.pavoSaleNumber ?? null,
          totalPrice: r.TotalPrice ?? r.totalPrice ?? null,
          errorMessage: r.ErrorMessage ?? r.errorMessage ?? null,
          rawSale: r.RawSaleJson ?? r.rawSaleJson ?? null,
        });
      });

      (async () => {
        const url = `${PAVO_API_URL}/api/PavoPayment/resolve-quicksale-result`;
        const body = {
          RequestId: requestId,
          DeviceCode: deviceCode,
          OrderNo: options.orderNo ?? requestId,
          CompanyId: options.companyId,
          BranchId: options.branchId ?? null,
          CallerConnectionId: callerConnectionId,
        };
        try {
          const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
          if (!res.ok) {
            // 503 -> cihaz offline; reconciliation yapılamaz -> unknown
            let msg = 'Reconciliation isteği başarısız';
            try {
              const data = await res.json();
              if (data?.Message) msg = data.Message;
              else if (data?.message) msg = data.message;
            } catch (_) {}
            console.warn(
              '[RestP2P] resolveQuickSaleResult HTTP',
              res.status,
              msg,
            );
            settle({
              status: 'unknown',
              requestId,
              errorMessage: msg,
            });
            return;
          }
          // 202 Accepted: cihaza push edildi, SignalR cevabını bekle.
          timeoutHandle = setTimeout(() => {
            settle({
              status: 'unknown',
              requestId,
              errorMessage: 'Pavo cihazından getSaleResult cevabı zaman aşımına uğradı',
            });
          }, timeoutMs);
        } catch (err: any) {
          console.error('[RestP2P] resolveQuickSaleResult network hata:', err);
          settle({
            status: 'unknown',
            requestId,
            errorMessage: err?.message || 'Ağ hatası',
          });
        }
      })();
    });
  }

  /**
   * İade/iptal DOĞRULAMASI — yalnız PavoSaleNumber elimizdeyken cihazdan gerçek
   * satış durumunu çeker. Backend saleNumber -> orijinal OrderNo (=RequestId)
   * çözer, `getSaleResult` zincirini tetikler ve sonucu `OnQuickSaleResultResolved`
   * ile döner.
   *
   * `getReturnableSale`'in aksine `getSaleResult` SAF SORGUDUR → cihazda
   * "iade edilemez / partial return" hata ekranı ÇIKMAZ.
   *
   * Akış: web -> POST /refund/verify-sale-by-number -> SignalR
   * `OnResolveQuickSaleResultRequest` -> Pavo getSaleResult -> POST
   * /resolve-quicksale-result-complete -> SignalR `OnQuickSaleResultResolved` -> web.
   */
  async verifySaleVoidByNumber(
    saleNumber: string,
    deviceCode: string,
    options: { timeoutMs?: number; companyId: string; branchId?: string | null },
  ): Promise<RestP2PResolveOutcome> {
    const timeoutMs = options.timeoutMs ?? RESOLVE_TIMEOUT_MS;
    // Bu doğrulama için YENİ korelasyon id'si (orijinal ödeme requestId'si DEĞİL).
    const requestId = generateUuid();
    const subId = `verify-sale-${requestId}`;

    const callerConnectionId =
      (await this.ensureSignalRConnected(options.companyId, options.branchId)) ?? null;

    return new Promise<RestP2PResolveOutcome>((resolve) => {
      let settled = false;
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

      const cleanup = () => {
        pavoSignalRService.removeQuickSaleResultResolvedCallback(subId);
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
      };

      const settle = (outcome: RestP2PResolveOutcome) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(outcome);
      };

      pavoSignalRService.onQuickSaleResultResolved(subId, (rid, result) => {
        if (rid !== requestId) return;
        if (!result || typeof result !== 'object') {
          settle({ status: 'unknown', requestId });
          return;
        }
        const r = result as any;
        const statusRaw = String(r.Status || r.status || 'unknown').toLowerCase();
        const status = ([
          'completed',
          'failed',
          'cancelled',
          'notfound',
          'unknown',
        ].includes(statusRaw)
          ? statusRaw
          : 'unknown') as RestP2PResolveOutcome['status'];
        settle({
          status,
          requestId,
          statusId: r.StatusId ?? r.statusId ?? null,
          pavoSaleNumber: r.PavoSaleNumber ?? r.pavoSaleNumber ?? null,
          totalPrice: r.TotalPrice ?? r.totalPrice ?? null,
          errorMessage: r.ErrorMessage ?? r.errorMessage ?? null,
          rawSale: r.RawSaleJson ?? r.rawSaleJson ?? null,
        });
      });

      (async () => {
        const url = `${PAVO_API_URL}/api/PavoPayment/refund/verify-sale-by-number`;
        const body = {
          RequestId: requestId,
          DeviceCode: deviceCode,
          SaleNumber: saleNumber,
          CompanyId: options.companyId,
          BranchId: options.branchId ?? null,
          CallerConnectionId: callerConnectionId,
        };
        try {
          const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
          if (!res.ok) {
            let msg = 'Satış durumu doğrulanamadı';
            let errorCode = `HTTP_${res.status}`;
            try {
              const data = await res.json();
              if (data?.Message) msg = data.Message;
              else if (data?.message) msg = data.message;
              if (data?.ErrorCode) errorCode = data.ErrorCode;
            } catch (_) {}
            console.warn('[RestP2P] verifySaleVoidByNumber HTTP', res.status, errorCode, msg);
            // ORDERNO_NOT_FOUND -> orijinal ödeme talebi yok (eski kayıt/direct REST);
            // DEVICE_OFFLINE -> cihaz kapalı. İkisi de "unknown" → çağıran karar verir.
            settle({ status: 'unknown', requestId, errorMessage: `${errorCode}: ${msg}` });
            return;
          }
          // 202 Accepted: cihaza push edildi, SignalR cevabını bekle.
          timeoutHandle = setTimeout(() => {
            settle({
              status: 'unknown',
              requestId,
              errorMessage: 'Pavo cihazından getSaleResult cevabı zaman aşımına uğradı',
            });
          }, timeoutMs);
        } catch (err: any) {
          console.error('[RestP2P] verifySaleVoidByNumber network hata:', err);
          settle({ status: 'unknown', requestId, errorMessage: err?.message || 'Ağ hatası' });
        }
      })();
    });
  }

  // ════════════════════════════════════════════════════════════════
  // F4 — Rest-P2P iade/iptal/sorgu metodlari
  //
  // Tum metodlar:
  //   1) ensureSignalRConnected ile callerConnectionId alir
  //   2) HTTP POST /api/PavoPayment/refund/<action> ile istegi backend'e yollar
  //   3) SignalR OnPavoRefundCompleted / OnPavoRefundFailed dinleyerek sonucu bekler
  //   4) Timeout veya error durumlarinda normalize edilmis sonuc doner
  // ════════════════════════════════════════════════════════════════

  /** Rest-P2P refund istek sonucu (unified). */
  private async runRefundRequest(
    action: RestP2PRefundAction,
    endpoint: string,
    body: Record<string, any>,
    options: { timeoutMs?: number; companyId?: string | null; branchId?: string | null } = {},
  ): Promise<RestP2PRefundOutcome> {
    const timeoutMs = options.timeoutMs ?? 180000;
    const requestId = typeof body.RequestId === 'string' && body.RequestId.length > 0
      ? body.RequestId
      : generateUuid();

    const callerConnectionId =
      body.CallerConnectionId ??
      (await this.ensureSignalRConnected(options.companyId ?? body.CompanyId, options.branchId ?? body.BranchId));

    const payload = {
      ...body,
      RequestId: requestId,
      CallerConnectionId: callerConnectionId ?? null,
    };

    return new Promise<RestP2PRefundOutcome>((resolve) => {
      const subId = `restp2p-refund-${requestId}`;
      let settled = false;
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

      const cleanup = () => {
        pavoSignalRService.removePavoRefundCallbacks(subId);
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
      };

      const settle = (outcome: RestP2PRefundOutcome) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(outcome);
      };

      pavoSignalRService.onPavoRefundCompleted(subId, (rid, act, result) => {
        if (rid !== requestId) return;
        if (act && act !== action) return;
        settle({ status: 'completed', requestId, action, result });
      });

      pavoSignalRService.onPavoRefundFailed(subId, (rid, act, errorCode, message) => {
        if (rid !== requestId) return;
        if (act && act !== action) return;
        settle({
          status: 'failed',
          requestId,
          action,
          errorCode: errorCode || 'UNKNOWN',
          message: message || 'Rest-P2P refund başarısız',
        });
      });

      (async () => {
        const fullUrl = `${PAVO_API_URL}${endpoint}`;
        try {
          console.log(`[RestP2P-Refund] 🔵 POST ${fullUrl}`, { action, requestId, callerConnectionId });
          // ⬇️ PAVO İSPAT: Cihaza giden isteğin GERÇEK HTTP gövdesi (action + payload).
          //    Backend bu isteği SignalR ile cihaza `Action`/`Payload` olarak iletir;
          //    cihaz da Pavo SDK intent'ine (cancelSale → pavopay.intent.action.cancel.sale)
          //    çevirir. Pavo'ya iletilecek gerçek JSON budur.
          console.log(
            `%c[PAVO İSPAT] CİHAZA GİDEN GERÇEK İSTEK → action="${action}"${action === 'cancelSale' ? ' = pavopay.intent.action.cancel.sale (SATIŞ İPTALİ / VOID)' : ''}`,
            'background:#b71c1c;color:#fff;font-weight:bold;padding:2px 6px;border-radius:3px;',
            { method: 'POST', url: fullUrl, requestBody: payload },
          );
          console.log('[PAVO İSPAT] requestBody JSON:', JSON.stringify(payload, null, 2));
          const res = await fetch(fullUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          console.log(`[RestP2P-Refund] 🔵 Response: ${res.status} ${res.statusText} (action=${action})`);

          if (!res.ok) {
            let message = 'Rest-P2P refund isteği başarısız';
            let rawBody: string | null = null;
            try {
              rawBody = await res.text();
              try {
                const bodyJson = JSON.parse(rawBody);
                if (bodyJson?.Message) message = bodyJson.Message;
                else if (bodyJson?.message) message = bodyJson.message;
              } catch (_) {
                if (rawBody && rawBody.length < 500) message = rawBody;
              }
            } catch (_) {
              // body okunamadı
            }
            const errorCode =
              res.status === 404 ? 'ENDPOINT_NOT_FOUND'
              : res.status === 503 ? 'DEVICE_OFFLINE'
              : `HTTP_${res.status}`;
            console.error(
              `[RestP2P-Refund] ❌ HTTP ${res.status} (${errorCode}) - action=${action}, requestId=${requestId}`,
              { url: fullUrl, message, rawBody },
            );
            settle({ status: 'failed', requestId, action, errorCode, message });
            return;
          }

          timeoutHandle = setTimeout(() => {
            console.warn(
              `[RestP2P-Refund] ⏱️ TIMEOUT - Pavo'dan OnPavoRefund${action}Completed/Failed event'i gelmedi (${timeoutMs}ms). ` +
              `requestId=${requestId}, action=${action}`,
            );
            settle({
              status: 'failed',
              requestId,
              action,
              errorCode: 'TIMEOUT',
              message: 'Pavo cihazından refund yanıtı alınamadı (timeout)',
            });
          }, timeoutMs);
        } catch (err: any) {
          console.error(`[RestP2P-Refund] ❌ NETWORK/FETCH hatası - ${fullUrl}:`, err);
          settle({
            status: 'failed',
            requestId,
            action,
            errorCode: 'NETWORK',
            message: err?.message || 'Ağ hatası',
          });
        }
      })();
    });
  }

  /**
   * Satış iptali (tam iade): Pavo'da tamamlanmış satışı iptal eder.
   * Pavo intent: pavopay.intent.action.cancel.sale
   */
  async requestCancelSale(request: RestP2PCancelSaleRequest, options?: { timeoutMs?: number }): Promise<RestP2PRefundOutcome> {
    return this.runRefundRequest('cancelSale', '/api/PavoPayment/refund/cancel-sale', {
      DeviceCode: request.deviceCode,
      SaleNumber: request.saleNumber,
      Amount: request.amount ?? null,
      // Pavo `Sale.IsVoid`: void (anlık iptal) işlemi olduğunu açıkça belirt.
      IsVoid: request.isVoid ?? true,
      CompanyId: request.companyId ?? null,
      BranchId: request.branchId ?? null,
      RequestId: request.requestId,
    }, { timeoutMs: options?.timeoutMs, companyId: request.companyId, branchId: request.branchId });
  }

  /**
   * İade edilebilir satış bilgilerini sorgular (returnableSale).
   * Pavo intent: pavopay.intent.action.get.returnable.sale
   */
  async requestGetReturnableSale(request: RestP2PGetReturnableSaleRequest, options?: { timeoutMs?: number }): Promise<RestP2PRefundOutcome> {
    return this.runRefundRequest('getReturnableSale', '/api/PavoPayment/refund/get-returnable-sale', {
      DeviceCode: request.deviceCode,
      SaleId: request.saleId ?? null,
      SaleNumber: request.saleNumber ?? null,
      SaleUid: request.saleUid ?? null,
      CompanyId: request.companyId ?? null,
      BranchId: request.branchId ?? null,
      RequestId: request.requestId,
    }, { timeoutMs: options?.timeoutMs, companyId: request.companyId, branchId: request.branchId });
  }

  /**
   * Normal kısmi iade (birden çok kalem ve/veya ödeme yöntemi).
   * Pavo intent: pavopay.intent.action.partial.return
   */
  async requestPartialReturn(request: RestP2PPartialReturnRequest, options?: { timeoutMs?: number }): Promise<RestP2PRefundOutcome> {
    return this.runRefundRequest('partialReturn', '/api/PavoPayment/refund/partial-return', {
      DeviceCode: request.deviceCode,
      RelatedSaleId: request.relatedSaleId,
      Items: request.items,
      PaymentInfos: request.paymentInfos ?? null,
      OrderNo: request.orderNo ?? null,
      CompanyId: request.companyId ?? null,
      BranchId: request.branchId ?? null,
      RequestId: request.requestId,
    }, { timeoutMs: options?.timeoutMs, companyId: request.companyId, branchId: request.branchId });
  }

  /**
   * Avans/ön ödeme iadesi (PYK iadesi gibi).
   * Pavo intent: pavopay.intent.action.partial.return.for.advance.sale
   */
  async requestPartialReturnForAdvance(
    request: RestP2PPartialReturnForAdvanceRequest,
    options?: { timeoutMs?: number },
  ): Promise<RestP2PRefundOutcome> {
    return this.runRefundRequest('partialReturnForAdvanceSale', '/api/PavoPayment/refund/partial-return-advance', {
      DeviceCode: request.deviceCode,
      RelatedSaleId: request.relatedSaleId,
      TotalRefundAmount: Number(request.totalRefundAmount.toFixed(2)),
      PaymentInfos: request.paymentInfos,
      SaleItems: request.saleItems ?? null,
      CompanyId: request.companyId ?? null,
      BranchId: request.branchId ?? null,
      RequestId: request.requestId,
    }, { timeoutMs: options?.timeoutMs, companyId: request.companyId, branchId: request.branchId });
  }

  /**
   * Avans satış (PYK "Bakiye Yükle (POS)").
   * Pavo intent: pavopay.intent.action.advance.sale
   */
  async requestAdvanceSale(
    request: RestP2PAdvanceSaleRequest,
    options?: { timeoutMs?: number },
  ): Promise<RestP2PRefundOutcome> {
    return this.runRefundRequest('advanceSale', '/api/PavoPayment/refund/advance-sale', {
      DeviceCode: request.deviceCode,
      Amount: Number(request.amount.toFixed(2)),
      Reason: request.reason,
      PaymentMediator: request.paymentMediator,
      OrderNo: request.orderNo ?? null,
      CustomerParty: request.customerParty ?? null,
      CompanyId: request.companyId ?? null,
      BranchId: request.branchId ?? null,
      RequestId: request.requestId,
    }, { timeoutMs: options?.timeoutMs, companyId: request.companyId, branchId: request.branchId });
  }

  /** Kullanıcı bekleme modalında iptal ederse çağrılır. */
  async cancelPayment(requestId: string): Promise<void> {
    try {
      await fetch(`${PAVO_API_URL}/api/PavoPayment/cancel-quicksale/${encodeURIComponent(requestId)}`, {
        method: 'POST',
      });
    } catch (err) {
      console.warn('[RestP2P] Cancel isteği hatası (yutuldu):', err);
    }
  }

  /**
   * 💰 Nakit ödeme için fire-and-forget: Pavo cihazına "nakit fişi kes"
   * isteği gönderir, SignalR cevap beklemez. Kasiyer zaten parayı elden almıştır.
   *
   * - POST başarılı → Promise resolve (cihaz isteği aldı, fişi arka planda keser)
   * - POST başarısız → reject (hata mesajı ile)
   * - SignalR cevabı background'da dispatcher üzerinden DB'yi günceller.
   */
  async sendCashFireAndForget(request: RestP2PPaymentRequest): Promise<{ requestId: string }> {
    const requestId = request.requestId && request.requestId.length > 0
      ? request.requestId
      : generateUuid();

    // Nakit fire-and-forget'te bile SignalR baglantisini acik tutariz; Pavo
    // bize arka planda "completed" yollar ve UI (badge, log) guncellenir.
    const callerConnectionId =
      request.callerConnectionId ??
      (await this.ensureSignalRConnected(request.companyId, request.branchId));

    const payload = {
      RequestId: requestId,
      DeviceCode: request.deviceCode,
      PaymentMode: 'cash',
      TotalAmount: Number(request.totalAmount.toFixed(2)),
      Parts: request.parts.map((p) => ({
        Method: p.method,
        Amount: Number(p.amount.toFixed(2)),
        Label: p.label ?? null,
        Order: p.order,
      })),
      ReceiptItemsJson: request.receiptItemsJson ?? null,
      CallerConnectionId: callerConnectionId,
      CompanyId: request.companyId ?? null,
      BranchId: request.branchId ?? null,
      ScreenId: request.screenId ?? null,
      CashierName: request.cashierName ?? null,
      SourceLabel: request.sourceLabel ?? null,
      // Phase 2: Nakit fire-and-forget'te de CustomerParty + NotificationPhone
      // gecebiliyor (tukenebilir bir mukellef secildiyse fise fatura bilgisi icin).
      CustomerParty: request.customerParty
        ? serializeCustomerParty(request.customerParty)
        : null,
      NotificationPhone: request.notificationPhone ?? null,
      // Mutfak entegrasyonu: Nakit fire-and-forget'te de fişe üst/alt bilgi basılır
      KitchenOrderCode: request.kitchenOrderCode ?? null,
      ReceiptCompanyName: request.receiptCompanyName ?? null,
      // Harici Odeme: nakit-fire-and-forget akisi disinda kalir ama tutarlilik
      // icin alan iletilir (fire-and-forget yontemi external'i set etmemeli).
      ExternalPayment: serializeExternalPayment(request.externalPayment),
    };

    const res = await fetch(`${PAVO_API_URL}/api/PavoPayment/create-quicksale-request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      let message = 'Nakit fişi Pavo cihazına iletilemedi';
      try {
        const body = await res.json();
        if (body?.Message) message = body.Message;
        else if (body?.message) message = body.message;
      } catch (_) {
        // json değil
      }
      throw new Error(message);
    }

    return { requestId };
  }
}

/**
 * PavoPay intent `saleObject.CustomerParty` ile ayni sema — backend'e JSON
 * olarak bu sekli gonderir, backend aynen cihaza iletir. Undefined alanlar
 * filtrelenir ki null/bos icerikler intent'e gereksizce yansimasin.
 */
function serializeCustomerParty(party: PavoPayCustomerParty): Record<string, any> {
  return {
    CustomerType: party.CustomerType ?? 1,
    FirstName: party.FirstName ?? '',
    MiddleName: '',
    FamilyName: party.FamilyName ?? '',
    CompanyName: party.CompanyName ?? '',
    TaxOfficeCode: party.TaxOfficeCode ?? '',
    TaxNumber: party.TaxNumber ?? '',
    Phone: party.Phone ?? '',
    EMail: party.EMail ?? '',
    Country: party.Country ?? 'Türkiye',
  };
}

/**
 * `RestP2PExternalPayment`'i backend `ExternalPaymentDto` (PascalCase) kalibina
 * cevirir. Eksik/null gelirse null doner ki payload'a `ExternalPayment: null`
 * yansisin.
 */
function serializeExternalPayment(
  ep?: RestP2PExternalPayment | null,
): Record<string, any> | null {
  if (!ep) return null;
  return {
    Type: ep.type,
    Mediator: ep.mediator,
    Brand: ep.brand,
    Amount: Number(ep.amount.toFixed(2)),
    ExternalReferenceText: ep.externalReferenceText ?? null,
  };
}

function generateUuid(): string {
  // crypto.randomUUID mevcut değilse fallback
  const c: any = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const restP2PPaymentService = new RestP2PPaymentService();
export default restP2PPaymentService;
